import { useState, useCallback } from 'react';
import { UserManager } from 'oidc-client-ts';

import { getOidcConfig, type OidcProvider } from '@/shared/config/oidc-config';

export const useOidcAuth = (provider: OidcProvider) => {
  const [isLoading, setIsLoading] = useState(false);
  const [userManager] = useState(() => new UserManager(getOidcConfig(provider)));

  const loginWithRedirect = useCallback(
    async (returnUrl?: string) => {
      try {
        setIsLoading(true);
        console.log(`Starting ${provider.toUpperCase()} OIDC login...`);

        await userManager.signinRedirect({ state: returnUrl });
      } catch (error) {
        console.error(`${provider.toUpperCase()} OIDC login failed:`, error);
        setIsLoading(false);
      }
    },
    [userManager, provider]
  );

  const handleCallback = useCallback(async () => {
    try {
      console.log(`Processing ${provider.toUpperCase()} OIDC callback...`);

      const user = await userManager.signinRedirectCallback();
      console.log(`${provider.toUpperCase()} OIDC user:`, user);

      return user;
    } catch (error) {
      console.error(`${provider.toUpperCase()} OIDC callback failed:`, error);
      throw error;
    }
  }, [userManager, provider]);

  const logout = useCallback(async () => {
    try {
      await userManager.signoutRedirect();
    } catch (error) {
      console.error(`${provider.toUpperCase()} OIDC logout failed:`, error);
    }
  }, [userManager, provider]);

  const getUser = useCallback(async () => {
    try {
      return await userManager.getUser();
    } catch (error) {
      console.error(`Failed to get ${provider.toUpperCase()} OIDC user:`, error);
      return null;
    }
  }, [userManager, provider]);

  const clearStaleState = useCallback(async () => {
    try {
      await userManager.clearStaleState();
    } catch (error) {
      console.error(`Failed to clear ${provider.toUpperCase()} stale state:`, error);
    }
  }, [userManager, provider]);

  return {
    loginWithRedirect,
    handleCallback,
    logout,
    getUser,
    clearStaleState,
    isLoading,
    userManager,
    provider
  };
};
